import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { ApiResponse } from '@/types'
import type { DailyParkingLog, VehicleType, DailyParkingPaymentMethod } from '@/types/parking'

// Query keys
export const dailyParkingLogKeys = {
  all: ['daily_parking_logs'] as const,
  lists: () => [...dailyParkingLogKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...dailyParkingLogKeys.lists(), filters] as const,
  details: () => [...dailyParkingLogKeys.all, 'detail'] as const,
  detail: (id: number) => [...dailyParkingLogKeys.details(), id] as const,
}

export interface DailyLogFilters {
  page?: number
  from?: string
  to?: string
}

// Fetch daily parking logs for a property
export function useDailyLogs(propertyId: number, filters: DailyLogFilters = {}) {
  return useQuery({
    queryKey: dailyParkingLogKeys.list({ propertyId, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (filters.page) params.page = filters.page
      if (filters.from) params.from = filters.from
      if (filters.to) params.to = filters.to
      const { data } = await api.get<ApiResponse<DailyParkingLog[]>>(
        `/api/properties/${propertyId}/daily_parking_logs`,
        { params }
      )
      return data
    },
    enabled: !!propertyId,
  })
}

// Create daily parking log
export interface CreateDailyLogInput {
  property_id: number
  license_plate: string
  vehicle_type: VehicleType
  vehicle_id?: number
  parker_name?: string
  parker_phone?: string
  fee: number
  payment_method?: DailyParkingPaymentMethod
  entered_at: string
  exited_at?: string
  notes?: string
}

export function useCreateDailyLog() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ property_id, ...rest }: CreateDailyLogInput) => {
      const { data } = await api.post<ApiResponse<DailyParkingLog>>(
        `/api/properties/${property_id}/daily_parking_logs`,
        { daily_parking_log: rest }
      )
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, dailyParkingLogKeys.lists())
    },
  })
}

// Update daily parking log
export interface UpdateDailyLogInput {
  id: number
  license_plate?: string
  vehicle_type?: VehicleType
  vehicle_id?: number | null
  parker_name?: string | null
  parker_phone?: string | null
  fee?: number
  payment_method?: DailyParkingPaymentMethod | null
  entered_at?: string
  exited_at?: string | null
  notes?: string | null
}

export function useUpdateDailyLog() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateDailyLogInput) => {
      const { data } = await api.patch<ApiResponse<DailyParkingLog>>(
        `/api/daily_parking_logs/${id}`,
        { daily_parking_log: input }
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, dailyParkingLogKeys.lists())
      invalidateDetailQueries(queryClient, dailyParkingLogKeys.detail(data.id))
    },
  })
}

// Delete daily parking log
export function useDeleteDailyLog() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/daily_parking_logs/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, dailyParkingLogKeys.lists())
    },
  })
}
